import { createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { absenceApi, AbsenceImportResponse, AbsenceUploadFileResponse } from 'app/services/edpApiV1/absence';
import { RootState } from 'app/store';

export type ImportPreviewTableRow = AbsenceUploadFileResponse['data'][number];

const rowsAdapter = createEntityAdapter<ImportPreviewTableRow>({
  selectId: row => row.tableId,
});

const initialState = rowsAdapter.getInitialState();

export const importSlice = createSlice({
  name: 'ui/absence/import',
  initialState,
  extraReducers: builder => {
    //При загрузке и импорте файла кладем данные в стор таблицы
    builder
      .addMatcher(
        absenceApi.endpoints.absenceFileUpload.matchFulfilled,
        (state, { payload }: PayloadAction<AbsenceUploadFileResponse>) => {
          rowsAdapter.setAll(state, payload.data);
        },
      )
      .addMatcher(absenceApi.endpoints.absenceFileUpload.matchRejected, () => rowsAdapter.getInitialState())
      .addMatcher(
        absenceApi.endpoints.absenceImport.matchFulfilled,
        (state, { payload }: PayloadAction<AbsenceImportResponse>) => {
          rowsAdapter.setAll(state, payload.data);
        },
      )
      .addMatcher(absenceApi.endpoints.absenceImport.matchRejected, () => rowsAdapter.getInitialState());
  },
  reducers: {
    setTableData: rowsAdapter.setAll,
    updateRow: rowsAdapter.updateOne,
    removeRow: rowsAdapter.removeOne,
    updateRows: rowsAdapter.updateMany,

    resetTable: () => rowsAdapter.getInitialState(),
  },
});

const { selectAll: selectAllRows, selectById: selectRowsById } = rowsAdapter.getSelectors<RootState>(
  state => state.ui.absence.import,
);

// Получить данные для импорта
const getAbsenceData = createSelector(selectAllRows, rows =>
  rows.reduce((acc: Array<{ tableId: number; personnelNumber: string; startedAt: string; endedAt: string }>, item) => {
    const { tableId, personnelNumber, startedAt, endedAt, errors } = item;
    return !errors?.employee ? [...acc, { tableId, personnelNumber, startedAt, endedAt }] : acc;
  }, []),
);

// Получить ошибки по сотруднику
const getEmployeeErrors = createSelector(selectAllRows, rows =>
  rows.reduce((acc: ImportPreviewTableRow[], item) => {
    const { errors } = item;
    return errors?.employee ? [...acc, item] : acc;
  }, []),
);

// Получить ошибки с дубликатами
const getDuplicateErrors = createSelector(selectAllRows, rows =>
  rows.reduce((acc: ImportPreviewTableRow[], item) => {
    const { errors } = item;
    return errors?.duplicate ? [...acc, item] : acc;
  }, []),
);

// Получить ошибки при пресечении периодов
const getIntervalErrors = createSelector(selectAllRows, rows =>
  rows.reduce((acc: ImportPreviewTableRow[], item) => {
    const { errors } = item;
    return errors?.interval ? [...acc, item] : acc;
  }, []),
);

// Получить ошибки с некорректными  данными
const getInvalidDateErrors = createSelector(selectAllRows, rows =>
  rows.reduce((acc: ImportPreviewTableRow[], item) => {
    const { errors } = item;
    if (!errors?.employee) {
      return errors?.date ? [...acc, item] : acc;
    }
    return acc;
  }, []),
);

// Получить ошибки с некорректными и пустыми данными
const getDateErrors = createSelector(selectAllRows, rows =>
  rows.reduce((acc: ImportPreviewTableRow[], item) => {
    const { errors, startedAt, endedAt } = item;
    if (!errors?.employee) {
      return errors?.date || !startedAt || !endedAt ? [...acc, item] : acc;
    }
    return acc;
  }, []),
);
export const selectors = {
  selectAllRows,
  getAbsenceData,
  selectRowsById,
  getDuplicateErrors,
  getInvalidDateErrors,
  getDateErrors,
  getEmployeeErrors,
  getIntervalErrors,
};
export const actions = importSlice.actions;
export const reducer = importSlice.reducer;
